/**
 * A tiny procedural sound engine built on the Web Audio API — no audio assets.
 * Everything is synthesized from oscillators + noise bursts. All calls are
 * guarded so a missing/blocked AudioContext (e.g. headless) is a silent no-op.
 *
 * Create/resume happens lazily on the first user gesture (see ensure()).
 */
import { launchTone, clashSpec, type ClashSpec } from './mix.ts';
import { loadMuted, saveMuted } from '../game/storage.ts';

type FinishKind = 'spinout' | 'ringout' | 'burst';

class SoundEngine {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private noiseBuffer: AudioBuffer | null = null;
  private whirOsc: OscillatorNode | null = null;
  private whirGain: GainNode | null = null;
  muted = false;

  /** Create (or resume) the audio context. Safe to call repeatedly. */
  ensure(): void {
    try {
      if (!this.ctx) {
        this.muted = loadMuted(); // honor the persisted preference
        const Ctor =
          window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
        if (!Ctor) return;
        this.ctx = new Ctor();
        this.master = this.ctx.createGain();
        this.master.gain.value = this.muted ? 0 : 0.5;
        this.master.connect(this.ctx.destination);
        this.noiseBuffer = this.makeNoise();
      }
      if (!this.muted && this.ctx.state === 'suspended') void this.ctx.resume();
    } catch {
      this.ctx = null; // give up silently
    }
  }

  setMuted(m: boolean): void {
    this.muted = m;
    if (this.master) this.master.gain.value = m ? 0 : 0.5;
    saveMuted(m);
  }

  toggleMute(): boolean {
    this.setMuted(!this.muted);
    return this.muted;
  }

  // --- public sfx ---------------------------------------------------------

  blip(): void {
    this.tone({ startFreq: 620, endFreq: 660, gain: 0.12, dur: 0.05 }, 'square');
  }

  countdown(step: number): void {
    if (step < 3) this.tone({ startFreq: 440, endFreq: 440, gain: 0.16, dur: 0.12 }, 'triangle');
    else this.tone({ startFreq: 300, endFreq: 920, gain: 0.2, dur: 0.28 }, 'sawtooth');
  }

  launch(power: number): void {
    this.tone(launchTone(power), 'sawtooth');
    this.noise(0.18, 0.06, 1400);
  }

  clash(impact: number, bigHit: boolean): void {
    const s: ClashSpec = clashSpec(impact, bigHit);
    this.noise(s.dur, s.gain, s.noiseFreq);
    if (s.thud) this.tone({ startFreq: 150, endFreq: 70, gain: 0.3, dur: 0.26 }, 'sine');
  }

  special(): void {
    this.tone({ startFreq: 380, endFreq: 1300, gain: 0.18, dur: 0.4 }, 'sawtooth');
    this.noise(0.25, 0.05, 2600);
  }

  finish(kind: FinishKind): void {
    if (kind === 'spinout') this.tone({ startFreq: 620, endFreq: 110, gain: 0.22, dur: 0.7 }, 'sine');
    else if (kind === 'ringout') {
      this.tone({ startFreq: 300, endFreq: 1200, gain: 0.2, dur: 0.4 }, 'sawtooth');
      this.noise(0.3, 0.08, 1800);
    } else {
      this.noise(0.5, 0.22, 700); // burst explosion
      this.tone({ startFreq: 120, endFreq: 50, gain: 0.32, dur: 0.5 }, 'sine');
    }
  }

  win(): void {
    [523, 659, 784, 1047].forEach((f, i) =>
      this.tone({ startFreq: f, endFreq: f, gain: 0.16, dur: 0.18 }, 'triangle', i * 0.12),
    );
  }

  lose(): void {
    [392, 311, 196].forEach((f, i) =>
      this.tone({ startFreq: f, endFreq: f, gain: 0.16, dur: 0.24 }, 'sine', i * 0.16),
    );
  }

  // --- continuous spin whir ----------------------------------------------

  /** Start the ambient spin whir (idempotent — stops any existing one first). */
  startWhir(): void {
    const ctx = this.ctx;
    if (!ctx || !this.master) return;
    this.stopWhir();
    this.whirOsc = ctx.createOscillator();
    this.whirGain = ctx.createGain();
    this.whirOsc.type = 'sawtooth';
    this.whirOsc.frequency.value = 90;
    this.whirGain.gain.value = 0;
    this.whirOsc.connect(this.whirGain).connect(this.master);
    this.whirOsc.start();
  }

  /** Track the whir to overall spin energy (0..1): higher = brighter/louder. */
  setWhir(intensity: number): void {
    if (!this.ctx || !this.whirOsc || !this.whirGain) return;
    const t = this.ctx.currentTime;
    const i = Math.max(0, Math.min(1, intensity));
    this.whirOsc.frequency.linearRampToValueAtTime(70 + i * 150, t + 0.1);
    this.whirGain.gain.linearRampToValueAtTime(0.012 + i * 0.05, t + 0.1);
  }

  stopWhir(): void {
    if (this.whirOsc) {
      try {
        this.whirOsc.stop();
      } catch {
        /* already stopped */
      }
      this.whirOsc.disconnect();
      this.whirGain?.disconnect();
    }
    this.whirOsc = null;
    this.whirGain = null;
  }

  // --- primitives ---------------------------------------------------------

  private tone(
    spec: { startFreq: number; endFreq: number; gain: number; dur: number },
    type: OscillatorType,
    delay = 0,
  ): void {
    const ctx = this.ctx;
    if (!ctx || this.muted || !this.master) return;
    const t = ctx.currentTime + delay;
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(spec.startFreq, t);
    if (spec.endFreq !== spec.startFreq) {
      osc.frequency.exponentialRampToValueAtTime(Math.max(1, spec.endFreq), t + spec.dur);
    }
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(spec.gain, t + 0.01);
    g.gain.exponentialRampToValueAtTime(0.0001, t + spec.dur);
    osc.connect(g).connect(this.master);
    osc.start(t);
    osc.stop(t + spec.dur + 0.02);
  }

  private noise(dur: number, gain: number, filterFreq: number): void {
    const ctx = this.ctx;
    if (!ctx || this.muted || !this.master || !this.noiseBuffer) return;
    const t = ctx.currentTime;
    const src = ctx.createBufferSource();
    src.buffer = this.noiseBuffer;
    const filter = ctx.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.value = filterFreq;
    filter.Q.value = 0.8;
    const g = ctx.createGain();
    g.gain.setValueAtTime(gain, t);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    src.connect(filter).connect(g).connect(this.master);
    src.start(t);
    src.stop(t + dur + 0.02);
  }

  private makeNoise(): AudioBuffer | null {
    const ctx = this.ctx;
    if (!ctx) return null;
    const buf = ctx.createBuffer(1, ctx.sampleRate * 0.6, ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
    return buf;
  }
}

export const sound = new SoundEngine();
